// Copyright 2026. WebPros International GmbH. All rights reserved.

import React from 'react';
import { connect } from 'react-redux';
import { RootState } from 'admin/core/store';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import * as computeResourceVmActions from 'common/modules/computeResourceVm/actions';
import * as computeResourceActions from 'admin/computeResource/actions';
import { LOADING_FLAGS } from 'common/modules/app/loadingFlags/constants';
import {
    Button,
    FormField,
    List,
    Translate,
} from '@plesk/ui-library';
import { Loader } from 'common/components';
import {
    INTENT_TYPE,
    SIZE,
} from 'common/constants';
import ButtonWithConfirmation from 'common/components/ButtonWithConfirmation';
import { EmptyView } from 'common/components/EmptyView/EmptyView';
import { Dialog } from 'common/components/Dialog/Dialog';
import SelectInput from 'common/components/Select/SelectInput';
import { ISelectOption } from 'common/components/SelectInput';

export type VmVGpuTabProps =
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

interface IGpuSelectOption extends ISelectOption {
    value: number;
    label: string;
}

const ATTACHED_COLUMNS = [
    {
        key: 'colGpuName',
        title: <Translate content="computeResourceVm.vgpu.columns.gpuName" />,
    },
    {
        key: 'colPlacementId',
        title: <Translate content="computeResourceVm.vgpu.columns.placementId" />,
    },
    {
        key: 'colAddress',
        title: <Translate content="computeResourceVm.vgpu.columns.address" />,
    },
    {
        key: 'colProfile',
        title: <Translate content="computeResourceVm.vgpu.columns.profile" />,
    },
    {
        key: 'colActions',
        title: '',
        width: '1%',
    },
];

const VmVGpuTab: React.FC<VmVGpuTabProps> = ({
    serverId,
    computeResourceId,
    vgpuPlacements,
    gpus,
    isLoading,
    isLoadingGpus,
    isAttaching,
    isDetaching,
    isServerProcessing,
    getServerVgpuPlacements,
    getGpus,
    attachVgpu,
    detachVgpu,
}) => {
    const [isDialogOpen, setDialogOpen] = React.useState(false);
    const [selectedGpu, setSelectedGpu] = React.useState<IGpuSelectOption | null>(null);

    React.useEffect(() => {
        if (serverId > 0) {
            getServerVgpuPlacements(serverId);
        }
    }, [serverId, getServerVgpuPlacements]);

    const openDialog = React.useCallback(() => {
        setDialogOpen(true);
        if (computeResourceId > 0) {
            getGpus(computeResourceId);
        }
    }, [computeResourceId, getGpus]);

    const availableGpuOptions: IGpuSelectOption[] = React.useMemo(() =>
        gpus
            .filter(gpu => gpu.is_partitioned && gpu.placement_addresses.some(p => !p.server))
            .map(gpu => ({
                value: gpu.id,
                label: `${gpu.name}${gpu.current_profile ? ` (${gpu.current_profile})` : ''}`,
            })),
    [gpus]);

    const handleAttach = React.useCallback(async () => {
        if (!selectedGpu) {
            return;
        }

        try {
            await attachVgpu(serverId, selectedGpu.value);
            setDialogOpen(false);
            setSelectedGpu(null);
        } catch {
            // Errors handled by global error handler.
        }
    }, [serverId, selectedGpu, attachVgpu]);

    const closeDialog = React.useCallback(() => {
        setDialogOpen(false);
        setSelectedGpu(null);
    }, []);

    const hasAttached = vgpuPlacements.length > 0;

    const attachedData = vgpuPlacements.map(placement => ({
        id: placement.id,
        colGpuName: placement.gpu?.name ?? '-',
        colPlacementId: placement.placement_id,
        colAddress: placement.address,
        colProfile: placement.gpu?.current_profile ?? '-',
        colActions: (
            <ButtonWithConfirmation
                isLoading={isDetaching || isServerProcessing}
                disabled={isServerProcessing}
                handleConfirm={() => detachVgpu(serverId, placement.id)}
                confirmationButtonText={<Translate content="computeResourceVm.vgpu.detachBtn" />}
                confirmationButtonGhost={false}
                buttonIntent={INTENT_TYPE.DANGER}
                buttonSize={SIZE.MD}
                translations={{
                    title: <Translate content="computeResourceVm.vgpu.detachBtn" />,
                    text: <Translate content="computeResourceVm.vgpu.detachConfirmation" />,
                    button: <Translate content="computeResourceVm.vgpu.detachBtn" />,
                    tooltip: isServerProcessing
                        ? <Translate content="computeResourceVm.vgpu.processingTooltip" />
                        : undefined,
                }}
            />
        ),
    }));

    return (
        <Loader isLoading={isLoading}>
            {hasAttached ? (
                <List
                    columns={ATTACHED_COLUMNS}
                    data={attachedData}
                    rowKey="id"
                />
            ) : (
                <EmptyView
                    title="computeResourceVm.vgpu.emptyView.title"
                    description="computeResourceVm.vgpu.emptyView.description"
                    buttonText="computeResourceVm.vgpu.emptyView.buttonText"
                    onButtonClick={openDialog}
                    hideIcon={true}
                />
            )}

            <Dialog
                isOpen={isDialogOpen}
                closeHandler={closeDialog}
                size="sm"
                heading={<Translate content="computeResourceVm.vgpu.dialog.title" />}
            >
                <FormField
                    label={<Translate content="computeResourceVm.vgpu.dialog.selectGpu" />}
                    vertical
                >
                    {({ getId }) => (
                        <SelectInput<IGpuSelectOption>
                            inputId={getId()}
                            menuPosition="fixed"
                            options={availableGpuOptions}
                            isLoading={isLoadingGpus}
                            isClearable={false}
                            value={selectedGpu}
                            onChange={(option) => setSelectedGpu(option as IGpuSelectOption)}
                            placeholder={<Translate content="computeResourceVm.vgpu.dialog.selectPlaceholder" />}
                        />
                    )}
                </FormField>

                <Button
                    intent={INTENT_TYPE.PRIMARY}
                    size={SIZE.LG}
                    fill={true}
                    disabled={!selectedGpu}
                    state={isAttaching ? 'loading' : undefined}
                    onClick={handleAttach}
                >
                    <Translate content="computeResourceVm.vgpu.dialog.attachBtn" />
                </Button>
            </Dialog>
        </Loader>
    );
};

const mapStateToProps = (state: RootState) => ({
    serverId: state.computeResourceVm.item.id,
    computeResourceId: state.computeResourceVm.item.compute_resource?.id ?? 0,
    vgpuPlacements: state.computeResourceVm.vgpuPlacements,
    gpus: state.computeResource.gpus,
    isLoading: state.app.loadingFlags.has(LOADING_FLAGS.SERVER_VGPU_PLACEMENTS),
    isLoadingGpus: state.app.loadingFlags.has(LOADING_FLAGS.COMPUTE_RESOURCE_GPUS),
    isAttaching: state.app.loadingFlags.has(LOADING_FLAGS.ATTACH_VGPU),
    isDetaching: state.app.loadingFlags.has(LOADING_FLAGS.DETACH_VGPU),
    isServerProcessing: state.computeResourceVm.item.is_processing,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    getServerVgpuPlacements: bindActionCreators(computeResourceVmActions.getServerVgpuPlacements, dispatch),
    getGpus: bindActionCreators(computeResourceActions.getGpus, dispatch),
    attachVgpu: bindActionCreators(computeResourceVmActions.attachVgpu, dispatch),
    detachVgpu: bindActionCreators(computeResourceVmActions.detachVgpu, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(VmVGpuTab);
